/**
 * @fileoverview Enlace de factura pendiente (Power BI o correo) en sessionStorage.
 * Conserva el token de `/facturas/:token` mientras el usuario inicia sesion, para retomarlo despues; su
 * origen (en un registro aparte, ligado al token, que el login no consume); y la marca de reintento que
 * limita a un solo viaje al login por rechazos de sesion.
 * @module pending-factura-link
 */

import {
  normalizarOrigenEnlace,
  type OrigenEnlace,
} from '../../materialidad/app/utils/origenEnlace';

/** Llave de sessionStorage donde se guarda `{ token, savedAt }`. */
export const PENDING_FACTURA_LINK_KEY = 'materialidad:pending-factura-link';

/** Vigencia del enlace pendiente: 30 minutos. */
export const PENDING_FACTURA_LINK_TTL_MS = 30 * 60 * 1000;

/** Solo base64url: un token con otro formato nunca se convierte en ruta (evita open redirect). */
const TOKEN_REGEX = /^[A-Za-z0-9_-]{1,256}$/;

/**
 * @function isValidFacturaLinkToken
 * @description Indica si el token de `/facturas/:token` tiene formato base64url admisible.
 */
export function isValidFacturaLinkToken(token: string | undefined): token is string {
  return token !== undefined && TOKEN_REGEX.test(token);
}

/**
 * Llave de sessionStorage del origen del enlace: `{ token, origen, savedAt }`. Es aparte del enlace
 * pendiente porque el login consume ese enlace y el origen debe sobrevivir hasta que la pagina del
 * enlace lo use (igual que la marca de reintento). Va ligado a su token: solo se lee para ese token.
 */
export const FACTURA_LINK_ORIGEN_KEY = 'materialidad:factura-link-origen';

/**
 * @function savePendingFacturaLink
 * @description Guarda el token con la hora actual y, si se conoce, el origen del enlace en su propio
 * registro (ver `FACTURA_LINK_ORIGEN_KEY`). Ignora tokens invalidos y fallos de sessionStorage. Un
 * origen que no sea uno de los permitidos se guarda como `Desconocido`; sin origen no se toca el que
 * ya hubiera.
 * @param {string} token - Token de `/facturas/:token`.
 * @param {OrigenEnlace} [origen] - Origen informado por el backend.
 */
export function savePendingFacturaLink(
  token: string,
  origen?: OrigenEnlace
): void {
  if (!TOKEN_REGEX.test(token)) return;
  try {
    sessionStorage.setItem(
      PENDING_FACTURA_LINK_KEY,
      JSON.stringify({ token, savedAt: Date.now() })
    );
    if (origen !== undefined) {
      sessionStorage.setItem(
        FACTURA_LINK_ORIGEN_KEY,
        JSON.stringify({
          token,
          origen: normalizarOrigenEnlace(origen),
          savedAt: Date.now(),
        })
      );
    }
  } catch {
    // Sin sessionStorage (modo privado o cuota) el enlace simplemente no se retoma.
  }
}

/**
 * @function clearPendingFacturaLink
 * @description Elimina el enlace pendiente. Nunca lanza.
 */
export function clearPendingFacturaLink(): void {
  try {
    sessionStorage.removeItem(PENDING_FACTURA_LINK_KEY);
  } catch {
    // Nada que limpiar si el almacenamiento no esta disponible.
  }
}

/**
 * @function tokenDeRegistro
 * @description Valida un registro crudo guardado: JSON con `{ token, savedAt }`, dentro de la vigencia
 * y con token base64url. Compartida por el enlace pendiente, la marca de reintento y el origen. Nunca lanza.
 * @param {string} crudo - Valor leido de sessionStorage.
 * @returns {string | null} El token si el registro es valido y vigente; si no, `null`.
 */
function tokenDeRegistro(crudo: string): string | null {
  try {
    const { token, savedAt } = JSON.parse(crudo) as {
      token?: unknown;
      savedAt?: unknown;
    };
    if (typeof token !== 'string' || typeof savedAt !== 'number') return null;
    if (Date.now() - savedAt > PENDING_FACTURA_LINK_TTL_MS) return null;
    if (!TOKEN_REGEX.test(token)) return null;
    return token;
  } catch {
    return null;
  }
}

/**
 * @function rutaDeEnlaceGuardado
 * @description Ruta del enlace pendiente guardado. Compartida por la lectura que consume y la que
 * solo consulta. Nunca lanza.
 * @param {string} crudo - Valor leido de sessionStorage.
 * @returns {string | null} `/facturas/<token>` si es valido y vigente; si no, `null`.
 */
function rutaDeEnlaceGuardado(crudo: string): string | null {
  const token = tokenDeRegistro(crudo);
  return token === null ? null : `/facturas/${token}`;
}

/**
 * @function consumePendingFacturaLinkPath
 * @description Lee y elimina el enlace pendiente (un solo uso).
 * @returns {string | null} `/facturas/<token>` si existe, esta vigente y es valido; si no, `null`.
 */
export function consumePendingFacturaLinkPath(): string | null {
  let crudo: string | null;
  try {
    crudo = sessionStorage.getItem(PENDING_FACTURA_LINK_KEY);
  } catch {
    return null;
  }
  if (crudo === null) return null;

  clearPendingFacturaLink();

  return rutaDeEnlaceGuardado(crudo);
}

/**
 * @function hasPendingFacturaLink
 * @description Indica si hay un enlace pendiente vigente y valido, sin consumirlo: el login lo usa
 * para avisar que hay una factura por abrir, y el enlace sigue disponible para despues del login.
 * Nunca lanza.
 * @returns {boolean} `true` si existe, esta vigente y es valido.
 */
export function hasPendingFacturaLink(): boolean {
  let crudo: string | null;
  try {
    crudo = sessionStorage.getItem(PENDING_FACTURA_LINK_KEY);
  } catch {
    return false;
  }
  return crudo !== null && rutaDeEnlaceGuardado(crudo) !== null;
}

/**
 * @function origenDeEnlace
 * @description Origen guardado para ese token: solo devuelve un origen guardado para el mismo token,
 * vigente (misma vigencia que el enlace pendiente) y con uno de los valores permitidos. No consume nada.
 * Nunca lanza.
 * @param {string} token - Token de `/facturas/:token`.
 * @returns {OrigenEnlace} El origen guardado para el token, o `Desconocido`.
 */
export function origenDeEnlace(token: string): OrigenEnlace {
  let crudo: string | null;
  try {
    crudo = sessionStorage.getItem(FACTURA_LINK_ORIGEN_KEY);
  } catch {
    return 'Desconocido';
  }
  if (crudo === null || tokenDeRegistro(crudo) !== token) return 'Desconocido';
  try {
    return normalizarOrigenEnlace((JSON.parse(crudo) as { origen?: unknown }).origen);
  } catch {
    return 'Desconocido';
  }
}

/**
 * @function origenDeEnlacePendiente
 * @description Origen del enlace pendiente vigente, sin consumirlo: el login lo usa para elegir el texto
 * de su aviso. Toma el token del enlace pendiente y lee su origen. Nunca lanza.
 * @returns {OrigenEnlace} El origen de ese enlace, o `Desconocido` si no hay enlace vigente o no se sabe.
 */
export function origenDeEnlacePendiente(): OrigenEnlace {
  let crudo: string | null;
  try {
    crudo = sessionStorage.getItem(PENDING_FACTURA_LINK_KEY);
  } catch {
    return 'Desconocido';
  }
  const token = crudo === null ? null : tokenDeRegistro(crudo);
  return token === null ? 'Desconocido' : origenDeEnlace(token);
}

/**
 * @function clearOrigenEnlace
 * @description Elimina el origen guardado. No toca el enlace pendiente ni la marca de reintento. Nunca lanza.
 */
export function clearOrigenEnlace(): void {
  try {
    sessionStorage.removeItem(FACTURA_LINK_ORIGEN_KEY);
  } catch {
    // Nada que limpiar si el almacenamiento no esta disponible.
  }
}

/**
 * Llave de sessionStorage de la marca de reintento: `{ token, savedAt }` del enlace que ya volvio una
 * vez al login por un rechazo de sesion. Es aparte del enlace pendiente porque el login consume ese
 * enlace y la marca debe sobrevivir hasta que la pagina del enlace vea el resultado del reintento.
 */
export const FACTURA_LINK_RETRY_KEY = 'materialidad:factura-link-retry';

/**
 * @function markFacturaLinkRetry
 * @description Marca el token como ya reintentado (un viaje al login por un rechazo de sesion), con la
 * hora actual y la misma vigencia que el enlace pendiente. Ignora tokens invalidos y fallos de
 * sessionStorage.
 * @param {string} token - Token de `/facturas/:token`.
 */
export function markFacturaLinkRetry(token: string): void {
  if (!TOKEN_REGEX.test(token)) return;
  try {
    sessionStorage.setItem(
      FACTURA_LINK_RETRY_KEY,
      JSON.stringify({ token, savedAt: Date.now() })
    );
  } catch {
    // Sin sessionStorage no hay tope: el comportamiento queda como antes de la marca.
  }
}

/**
 * @function hasFacturaLinkRetry
 * @description Indica si el token ya volvio una vez al login: la marca existe, esta vigente, es valida
 * y es de ese mismo token. No la consume. Nunca lanza.
 * @param {string} token - Token de `/facturas/:token`.
 * @returns {boolean} `true` si el token esta marcado.
 */
export function hasFacturaLinkRetry(token: string): boolean {
  let crudo: string | null;
  try {
    crudo = sessionStorage.getItem(FACTURA_LINK_RETRY_KEY);
  } catch {
    return false;
  }
  return crudo !== null && tokenDeRegistro(crudo) === token;
}

/**
 * @function clearFacturaLinkRetry
 * @description Elimina la marca de reintento. No toca el enlace pendiente. Nunca lanza.
 */
export function clearFacturaLinkRetry(): void {
  try {
    sessionStorage.removeItem(FACTURA_LINK_RETRY_KEY);
  } catch {
    // Nada que limpiar si el almacenamiento no esta disponible.
  }
}
